import React, { useState, useCallback, useEffect } from 'react';
import { Upload, FileText, Trash2, ChevronDown, ChevronRight, AlertCircle, Loader2, CheckCircle2, FlaskConical } from 'lucide-react';
import { Card } from '../ui/card';
import { useSidebar } from '../../contexts/SidebarContext';
import { erstpruefungService } from '../../services/erstpruefungService';

/* ──────────────── File Upload Area ──────────────── */
const FileUploadArea = ({ onFileSelected, isUploading }) => {
  const [dragOver, setDragOver] = useState(false);

  const handleFile = useCallback((file) => {
    if (!file || file.type !== 'application/pdf') return;
    onFileSelected(file);
  }, [onFileSelected]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors cursor-pointer ${
        dragOver ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-green-400 hover:bg-gray-50'
      }`}
      onClick={() => !isUploading && document.getElementById('erstpruefung-file-input').click()}
    >
      <input
        id="erstpruefung-file-input"
        type="file"
        accept=".pdf"
        className="hidden"
        onChange={(e) => handleFile(e.target.files[0])}
      />
      {isUploading ? (
        <>
          <Loader2 className="h-12 w-12 mx-auto mb-4 text-green-500 animate-spin" />
          <p className="text-lg font-medium text-gray-700">PDF wird verarbeitet...</p>
          <p className="text-sm text-gray-500 mt-2">Dies kann bis zu 2 Minuten dauern</p>
        </>
      ) : (
        <>
          <Upload className={`h-12 w-12 mx-auto mb-4 ${dragOver ? 'text-green-500' : 'text-gray-400'}`} />
          <p className="text-lg font-medium text-gray-700">Erstprüfung PDF hochladen</p>
          <p className="text-sm text-gray-500 mt-2">Drag & Drop oder klicken zum Auswählen (.pdf)</p>
        </>
      )}
    </div>
  );
};

/* ──────────────── Detail View ──────────────── */
const DetailRow = ({ label, value }) => {
  if (!value && value !== 0) return null;
  return (
    <div className="flex justify-between py-1.5 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900">{typeof value === 'number' ? value.toFixed(2) : value}</span>
    </div>
  );
};

const ErstpruefungCard = ({ doc, onDelete, onToggle, isOpen }) => {
  const mat = doc.materialInfo || {};
  const binder = doc.binderContent || {};
  const docInfo = doc.documentInfo || {};

  return (
    <Card className="mb-3 overflow-hidden">
      <div
        className="flex items-center justify-between px-5 py-3.5 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => onToggle(doc._id)}
      >
        <div className="flex items-center gap-3 min-w-0">
          {isOpen ? <ChevronDown className="h-4 w-4 text-green-600 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />}
          <FileText className="h-5 w-5 text-green-600 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">{mat.materialType || doc.filename}</p>
            <p className="text-xs text-gray-500 truncate">
              {mat.productionLocation && `${mat.productionLocation} · `}{docInfo.date ? new Date(docInfo.date).toLocaleDateString('de-DE') : new Date(doc.uploadedAt).toLocaleDateString('de-DE')}
            </p>
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(doc._id); }}
          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
          title="Löschen"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {isOpen && (
        <div className="px-5 pb-4 border-t border-gray-100 pt-3 grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Document Info */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Dokument</h4>
            <DetailRow label="Typ" value={docInfo.documentType || docInfo.type} />
            <DetailRow label="Nr." value={docInfo.documentNumber} />
            <DetailRow label="Datum" value={docInfo.date} />
            <DetailRow label="Dateiname" value={doc.filename} />
          </div>

          {/* Material + Binder Info */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Bindemittel</h4>
            <DetailRow label="Frischbindemittel" value={binder.freshBinder != null ? `${binder.freshBinder} %` : null} />
            <DetailRow label="Typ" value={binder.freshBinderType} />
            <DetailRow label="Aus RA" value={binder.recycledBinder != null ? `${binder.recycledBinder} %` : null} />
            <DetailRow label="Gesamt" value={binder.totalBinder != null ? `${binder.totalBinder} %` : null} />
            <DetailRow label="Result. Bindemittel" value={binder.resultierendesBindemittel != null ? `${binder.resultierendesBindemittel} %` : null} />
            <DetailRow label="Result. Typ" value={binder.resultierendesBindemittelType} />
          </div>

          {/* Rohdichte */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Kennwerte</h4>
            <DetailRow label="Rohdichte" value={doc.rohdichte != null ? `${doc.rohdichte} g/cm³` : null} />
          </div>

          {/* Aggregate Composition */}
          <div className="md:col-span-3">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Kornzusammensetzung</h4>
            {(doc.aggregateComposition || []).length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wider">
                      <th className="py-1.5 pr-3">Nr</th>
                      <th className="py-1.5 pr-3">Bezeichnung</th>
                      <th className="py-1.5 pr-3">Lieferkörnung</th>
                      <th className="py-1.5 pr-3">Bezugsquelle</th>
                      <th className="py-1.5 pr-3">Hersteller</th>
                      <th className="py-1.5 text-right">M.-%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doc.aggregateComposition.map((agg, i) => (
                      <tr key={i} className="border-b border-gray-50">
                        <td className="py-1.5 pr-3 font-mono text-gray-600">{agg.nr}</td>
                        <td className="py-1.5 pr-3 text-gray-900">{agg.bezeichnung || '–'}</td>
                        <td className="py-1.5 pr-3 text-gray-600">{agg.lieferkornung || '–'}</td>
                        <td className="py-1.5 pr-3 text-gray-600">{agg.bezugsquelle || agg.gewinnungsstatte || '–'}</td>
                        <td className="py-1.5 pr-3 text-gray-600">{agg.hersteller || '–'}</td>
                        <td className="py-1.5 text-right font-medium text-gray-900">{agg.massPercentage} %</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-xs text-gray-400 italic">Keine Daten</p>
            )}
          </div>

          {/* Asphaltgranulat */}
          {doc.asphaltgranulat && Array.isArray(doc.asphaltgranulat) && doc.asphaltgranulat.length > 0 && (
            <div className="md:col-span-3">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Asphaltgranulat</h4>
              <div className="space-y-0.5">
                {doc.asphaltgranulat.map((g, i) => (
                  <div key={i} className="flex justify-between text-sm py-1 border-b border-gray-50">
                    <span className="text-gray-500">{g.nr} – {g.bezeichnung}</span>
                    <span className="font-medium text-gray-900">{g.massPercentage} %</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

/* ──────────────── Main Component ──────────────── */
export const Erstpruefungen = () => {
  const { isExpanded } = useSidebar();
  const leftMargin = isExpanded ? 'ml-64' : 'ml-16';

  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [openId, setOpenId] = useState(null);

  // Fetch all Erstprüfungen on mount
  const fetchDocuments = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await erstpruefungService.getAll();
      setDocuments(result.data || []);
    } catch (err) {
      setError('Fehler beim Laden der Erstprüfungen');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Upload handler
  const handleFileSelected = useCallback(async (file) => {
    try {
      setIsUploading(true);
      setError(null);
      setSuccessMsg(null);

      const result = await erstpruefungService.upload(file);

      if (result.success) {
        setSuccessMsg(`"${file.name}" erfolgreich hochgeladen und gespeichert`);
        await fetchDocuments();
        // Auto-open the newly uploaded document
        if (result.data?._id) setOpenId(result.data._id);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Hochladen der Erstprüfung');
      console.error(err);
    } finally {
      setIsUploading(false);
    }
  }, [fetchDocuments]);

  // Delete handler
  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Erstprüfung wirklich löschen?')) return;
    try {
      await erstpruefungService.delete(id);
      setDocuments((prev) => prev.filter((d) => d._id !== id));
      if (openId === id) setOpenId(null);
    } catch (err) {
      setError('Fehler beim Löschen');
      console.error(err);
    }
  }, [openId]);

  const toggleOpen = useCallback((id) => {
    setOpenId((prev) => (prev === id ? null : id));
  }, []);

  // Auto-dismiss success message
  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  return (
    <div className={`${leftMargin} min-h-screen bg-gray-50 transition-all duration-300`}>
      <div className="p-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <FlaskConical className="h-7 w-7 text-green-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Erstprüfungen</h1>
            <p className="text-sm text-gray-500">Erstprüfungen hochladen, extrahieren und in der Datenbank speichern</p>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-4 flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">✕</button>
          </div>
        )}
        {successMsg && (
          <div className="mb-4 flex items-center gap-2 px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">
            <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
            <span>{successMsg}</span>
          </div>
        )}

        {/* Upload Area */}
        <div className="mb-6">
          <FileUploadArea onFileSelected={handleFileSelected} isUploading={isUploading} />
        </div>

        {/* Document List */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            Gespeicherte Erstprüfungen {!isLoading && <span className="text-sm font-normal text-gray-400">({documents.length})</span>}
          </h2>

          {isLoading ? (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              <span>Lade Erstprüfungen...</span>
            </div>
          ) : documents.length === 0 ? (
            <Card className="p-8 text-center text-gray-400">
              <FileText className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>Noch keine Erstprüfungen gespeichert</p>
            </Card>
          ) : (
            documents.map((doc) => (
              <ErstpruefungCard
                key={doc._id}
                doc={doc}
                onDelete={handleDelete}
                onToggle={toggleOpen}
                isOpen={openId === doc._id}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Erstpruefungen;
